/**
 * IMPL-031 (2026-06-22) · Calculadora de Ahorro pública /calculadora
 * IMPL-033 (2026-06-23) · Referencias institucionales, P6 valor adicional, piloto, intereses
 * IMPL-034 (2026-06-29) · Tema CLARO unificado con landing · Logo MOBILSAFE oficial · Panel lateral grande con contorno
 *
 * Flujo 6 pantallas (P0→P6) sin auth, todo client-side.
 * Captura de leads en /api/leads cuando el usuario activa CTA reunión o PDF.
 *
 * Diseño: fondo blanco + verde #10b981 + azul marino #1e3a5f para CTA secundario.
 */
import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { trackFunnelEvent } from "../../utils/analytics";
import {
  Check, ChevronLeft, ChevronRight, Plus, Minus, Loader2, X,
  Car, Truck, Package, Shuffle, Phone, Leaf, Fuel, Wrench, Shield, TrendingUp,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo oficial MOBILSAFE — mismo patrón que Landing.jsx (con fallback verde si CloudFront bloquea)
const MOBILSAFE_LOGO = "/logo-mobilsafe.png";

// ---- Defaults y tarifas ----
const TIPOS_VEHICULO = [
  { value: "coches", label: "Coches", icon: Car, emoji: "🚗" },
  { value: "furgonetas", label: "Furgonetas", icon: Truck, emoji: "🚐" },
  { value: "camiones", label: "Camiones", icon: Package, emoji: "🚛" },
  { value: "mixta", label: "Mixta", icon: Shuffle, emoji: "🔀" },
];

const DEFAULTS = {
  coches:     { km: 25000,  consumo: 7,  mant: 800,  costeSin: 4500 },
  furgonetas: { km: 35000,  consumo: 10, mant: 1200, costeSin: 4500 },
  camiones:   { km: 120000, consumo: 28, mant: 4500, costeSin: 12000 },
  mixta:      { km: 40000,  consumo: 12, mant: 1500, costeSin: 4500 },
};

// IMPL-033 · catálogo de intereses adicionales del modal reunión
const INTERESES_OPTIONS = [
  "Tacógrafo digital y gestión de infracciones",
  "Optimización de rutas",
  "Estudio de electrificación de flota",
  "Conexión con ERP/TMS (APIs)",
  "Informes personalizados para dirección",
  "Certificado CAES — ingresos por carbono",
];

const eur = (n) => {
  const safe = Math.max(0, Number.isFinite(n) ? Math.round(n) : 0);
  // useGrouping: 'always' fuerza separador de miles aun en Chromium con ICU 'min2'
  return new Intl.NumberFormat("es-ES", { useGrouping: "always", maximumFractionDigits: 0 }).format(safe) + " €";
};

// ============================================
// Logo MOBILSAFE compartido (mismo patrón que Landing.jsx)
// ============================================
const MobilsafeLogoHeader = () => (
  <div className="flex items-center gap-3">
    <img
      src={MOBILSAFE_LOGO}
      alt="MOBILSAFE"
      className="h-10 md:h-12"
      onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
    />
    <div className="hidden items-center gap-2">
      <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl flex items-center justify-center shadow-lg">
        <Leaf className="w-6 h-6 text-white" />
      </div>
      <div>
        <h1 className="text-xl font-black tracking-tight text-slate-900">MOBILSAFE</h1>
        <p className="text-[10px] text-emerald-600 tracking-widest font-semibold">FLEET TECHNOLOGY</p>
      </div>
    </div>
    <div className="border-l border-slate-200 pl-3 ml-1 hidden sm:block">
      <p className="text-xs text-emerald-600 font-semibold tracking-wide uppercase">Calculadora de Ahorro</p>
    </div>
  </div>
);

// ---- Componente principal ----
const CalculadoraAhorro = () => {
  const [step, setStep] = useState(0);
  const [nVehiculos, setNVehiculos] = useState(25);
  const [tipoVehiculo, setTipoVehiculo] = useState("furgonetas");
  const [km, setKm] = useState(DEFAULTS.furgonetas.km);
  const [consumo, setConsumo] = useState(DEFAULTS.furgonetas.consumo);
  const [precioCombustible, setPrecioCombustible] = useState(1.65);
  const [costeMant, setCosteMant] = useState(DEFAULTS.furgonetas.mant);
  const [nSiniestros, setNSiniestros] = useState(null);
  const [costeSiniestro, setCosteSiniestro] = useState(DEFAULTS.furgonetas.costeSin);

  // IMPL-062 · evento entry al montar la calculadora (denominador del funnel)
  useEffect(() => {
    trackFunnelEvent("calculadora", "entry");
  }, []);

  // Modales
  const [modalOpen, setModalOpen] = useState(null); // null | 'reunion' | 'pdf'
  const [modalDesde, setModalDesde] = useState(null); // 'P2'|'P3'|'P4'|'P5'|'P6'

  // Cambiar tipo de vehículo aplica defaults
  const onChangeTipo = (t) => {
    setTipoVehiculo(t);
    const d = DEFAULTS[t];
    setKm(d.km);
    setConsumo(d.consumo);
    setCosteMant(d.mant);
    setCosteSiniestro(d.costeSin);
    setNSiniestros(null);
  };

  // ---- Cálculos ----
  const calc = useMemo(() => {
    const n = Math.max(1, nVehiculos || 1);
    const kmS = Math.max(1, km || 1);
    const consS = Math.max(1, consumo || 1);
    const precioS = Math.max(0.01, precioCombustible || 0.01);
    const mantS = Math.max(1, costeMant || 1);
    const sinDefault = Math.round(n * 0.12);
    const nSinReal = nSiniestros !== null && nSiniestros >= 0 ? nSiniestros : sinDefault;
    const costeSinS = Math.max(1, costeSiniestro || 1);

    // IMPL-033 · cálculo explícito P2 (litros año, coste total, ahorro 12%)
    const litrosAnio = Math.round((kmS * consS / 100) * n);
    const costeCombustibleTotal = Math.round(litrosAnio * precioS);
    const ahorroCombustible = Math.round(costeCombustibleTotal * 0.12);
    const litrosAhorro = Math.round(litrosAnio * 0.12);

    const ahorroMantenimiento = Math.round(n * mantS * 0.15);
    const ahorroSeguridad = Math.round(nSinReal * costeSinS * 0.30);

    // IMPL-033 · total = SOLO las 3 áreas (sin base sector)
    const ahorroTotal = Math.max(0, ahorroCombustible + ahorroMantenimiento + ahorroSeguridad);
    const ahorroSoFar = Math.max(0,
        (step >= 2 ? ahorroCombustible : 0)
      + (step >= 3 ? ahorroMantenimiento : 0)
      + (step >= 4 ? ahorroSeguridad : 0)
    );
    const siniestrosMenos = Math.round(nSinReal * 0.30);

    return {
      ahorroCombustible, ahorroMantenimiento, ahorroSeguridad,
      ahorroTotal, ahorroSoFar,
      litrosAnio,
      costeCombustibleTotal,
      litrosAhorro,
      nSinReal,
      siniestrosMenos,
      nVehiculos: n,
    };
  }, [nVehiculos, tipoVehiculo, km, consumo, precioCombustible, costeMant, nSiniestros, costeSiniestro, step]);

  const openModalReunion = (desde) => { setModalDesde(desde); setModalOpen("reunion"); };
  const openModalPDF = () => { setModalDesde(`P${step}`); setModalOpen("pdf"); };

  // Barra de progreso (0%..100%) según step (0..6 con IMPL-033)
  const progressPct = (step / 6) * 100;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800" data-testid="calculadora-ahorro-view">
      {/* Header claro con logo oficial */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <MobilsafeLogoHeader />
        </div>
        <div className="h-1 bg-slate-200">
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${progressPct}%`, background: "#10b981" }}
            data-testid="calc-progress-bar"
          />
        </div>
      </header>

      {/* Body */}
      {step === 0 ? (
        <PantallaEntrada onStart={() => setStep(1)} />
      ) : (
        <div className="flex flex-col md:flex-row max-w-7xl mx-auto">
          {/* Panel lateral móvil (arriba) */}
          <PanelLateralMobile step={step} calc={calc} />
          {/* Panel lateral desktop (sticky izquierda) */}
          <PanelLateral step={step} calc={calc} />

          {/* Contenido derecho */}
          <main className="flex-1 px-6 md:px-10 py-8" data-testid={`calc-paso-${step}`}>
            <div className="max-w-3xl mx-auto">
              {step === 1 && (
                <PasoFlotaBase
                  nVehiculos={nVehiculos} setNVehiculos={setNVehiculos}
                  tipoVehiculo={tipoVehiculo} onChangeTipo={onChangeTipo}
                  onBack={() => setStep(0)} onNext={() => setStep(2)}
                />
              )}
              {step === 2 && (
                <PasoCombustible
                  km={km} setKm={setKm}
                  consumo={consumo} setConsumo={setConsumo}
                  precioCombustible={precioCombustible} setPrecioCombustible={setPrecioCombustible}
                  calc={calc}
                  onBack={() => setStep(1)} onNext={() => setStep(3)}
                  onReunion={() => openModalReunion("P2")}
                />
              )}
              {step === 3 && (
                <PasoMantenimiento
                  costeMant={costeMant} setCosteMant={setCosteMant}
                  ahorroSoFar={calc.ahorroSoFar}
                  onBack={() => setStep(2)} onNext={() => setStep(4)}
                  onReunion={() => openModalReunion("P3")}
                />
              )}
              {step === 4 && (
                <PasoSeguridad
                  nSiniestros={nSiniestros} setNSiniestros={setNSiniestros}
                  sinDefault={Math.round(nVehiculos * 0.12)}
                  costeSiniestro={costeSiniestro} setCosteSiniestro={setCosteSiniestro}
                  siniestrosMenos={calc.siniestrosMenos}
                  ahorroSeguridad={calc.ahorroSeguridad}
                  ahorroSoFar={calc.ahorroSoFar}
                  onBack={() => setStep(3)} onNext={() => setStep(5)}
                  onReunion={() => openModalReunion("P4")}
                />
              )}
              {step === 5 && (
                <PasoResumen
                  calc={calc}
                  onBack={() => setStep(4)}
                  onVerMas={() => setStep(6)}
                  onReunion={() => openModalReunion("P5")}
                  onPDF={openModalPDF}
                />
              )}
              {step === 6 && (
                <PasoValorAdicional
                  onBack={() => setStep(5)}
                  onReunion={() => openModalReunion("P6")}
                />
              )}
            </div>
          </main>
        </div>
      )}

      {/* Modal Reunión */}
      {modalOpen === "reunion" && (
        <ModalReunion
          modalDesde={modalDesde}
          calc={calc}
          datosLead={{
            num_vehiculos: nVehiculos,
            tipo_vehiculo: tipoVehiculo,
            ahorro_combustible: modalDesde === "P2" || step >= 2 ? calc.ahorroCombustible : null,
            ahorro_mantenimiento: modalDesde === "P3" || step >= 3 ? calc.ahorroMantenimiento : null,
            ahorro_seguridad: modalDesde === "P4" || step >= 4 ? calc.ahorroSeguridad : null,
            ahorro_total: calc.ahorroSoFar,
          }}
          onClose={() => { setModalOpen(null); setModalDesde(null); }}
        />
      )}
      {modalOpen === "pdf" && (
        <ModalPDF
          calc={calc}
          datosLead={{
            num_vehiculos: nVehiculos,
            tipo_vehiculo: tipoVehiculo,
            ahorro_combustible: calc.ahorroCombustible,
            ahorro_mantenimiento: calc.ahorroMantenimiento,
            ahorro_seguridad: calc.ahorroSeguridad,
            ahorro_total: calc.ahorroTotal,
          }}
          onClose={() => { setModalOpen(null); setModalDesde(null); }}
          onSwitchReunion={() => { setModalOpen("reunion"); setModalDesde("P5"); }}
        />
      )}
    </div>
  );
};

// ============================================
// PANELES Y PASOS
// ============================================

// IMPL-034 · Panel lateral DESKTOP — grande con contorno
const PanelLateral = ({ step, calc }) => {
  const items = [
    { Icon: Fuel,   label: "Combustible",    val: calc.ahorroCombustible,   done: step >= 2, color: "text-amber-600", iconBg: "bg-amber-50" },
    { Icon: Wrench, label: "Mantenimiento",  val: calc.ahorroMantenimiento, done: step >= 3, color: "text-violet-600", iconBg: "bg-violet-50" },
    { Icon: Shield, label: "Seguridad",      val: calc.ahorroSeguridad,     done: step >= 4, color: "text-rose-600",   iconBg: "bg-rose-50" },
  ];
  return (
    <aside
      className="hidden md:block flex-shrink-0 sticky self-start"
      style={{ width: "340px", top: "85px", padding: "24px 16px" }}
      data-testid="calc-panel-lateral"
    >
      <div
        className="bg-white rounded-2xl border-2 border-emerald-200 shadow-lg shadow-emerald-500/10 overflow-hidden"
        style={{ background: "linear-gradient(180deg, #f0fdf4 0%, #ffffff 30%)" }}
      >
        <div className="px-5 pt-5 pb-2">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
            <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">Ahorro acumulado</p>
          </div>
          <p className="text-5xl lg:text-6xl font-black leading-none text-emerald-600 tracking-tight" data-testid="panel-ahorro-sofar">
            {eur(calc.ahorroSoFar)}
          </p>
          <p className="text-xs text-slate-500 mt-2">Calculado para {calc.nVehiculos} vehículos</p>
        </div>

        <div className="border-t border-emerald-100 mx-5 my-3"></div>

        <div className="px-5 pb-5 space-y-3">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`calc-panel-area-${i}`}>
              <div className={`${it.iconBg} w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0`}>
                <it.Icon className={`w-4 h-4 ${it.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500">{it.label}</p>
                {it.done ? (
                  <p className="text-base font-bold text-slate-900">{eur(it.val)}</p>
                ) : (
                  <p className="text-xs italic text-slate-400">— pendiente</p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-slate-50 border-t border-slate-100 px-5 py-3">
          <p className="text-[11px] text-slate-500 leading-relaxed">
            🧪 <strong className="text-slate-700">Piloto de 45-60 días</strong> antes de cualquier despliegue total.
          </p>
        </div>
      </div>
    </aside>
  );
};

// IMPL-034 · Panel lateral MÓVIL (arriba antes del contenido, compact)
const PanelLateralMobile = ({ step, calc }) => {
  const items = [
    { Icon: Fuel,   label: "Comb.",    val: calc.ahorroCombustible,   done: step >= 2 },
    { Icon: Wrench, label: "Mant.",    val: calc.ahorroMantenimiento, done: step >= 3 },
    { Icon: Shield, label: "Seg.",     val: calc.ahorroSeguridad,     done: step >= 4 },
  ];
  return (
    <div className="md:hidden px-4 pt-4" data-testid="calc-panel-lateral-mobile">
      <div
        className="bg-white rounded-xl border-2 border-emerald-200 shadow-md shadow-emerald-500/10 p-4"
        style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #ffffff 50%)" }}
      >
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Ahorro acumulado</p>
            <p className="text-3xl font-black leading-tight text-emerald-600 tracking-tight">{eur(calc.ahorroSoFar)}</p>
          </div>
          <p className="text-[10px] text-slate-500 text-right">{calc.nVehiculos} vehículos</p>
        </div>
        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-emerald-100">
          {items.map((it, i) => (
            <div key={i} className="text-center">
              <it.Icon className="w-3.5 h-3.5 text-slate-400 mx-auto mb-0.5" />
              <p className="text-[10px] text-slate-500">{it.label}</p>
              {it.done ? (
                <p className="text-xs font-bold text-slate-900 truncate">{eur(it.val)}</p>
              ) : (
                <p className="text-[10px] italic text-slate-400">—</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---- P0 ----
const PantallaEntrada = ({ onStart }) => (
  <main className="max-w-3xl mx-auto px-6 py-14 md:py-20 text-center" data-testid="calc-paso-0">
    <h1 className="text-4xl md:text-5xl font-black leading-tight mb-4 text-slate-900">
      ¿Cuánto puede ahorrar <span className="text-emerald-600">tu flota</span>?
    </h1>
    <p className="text-slate-600 text-lg mb-10 max-w-2xl mx-auto">
      Calcula en 2 minutos el ahorro real en combustible, mantenimiento y seguridad. Sin registrarte. Sin compromiso.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10 text-left">
      {[
        "Sin registro ni email",
        "Resultados en menos de 2 minutos",
        "Basado en benchmarks del sector",
      ].map((txt, idx) => (
        <div key={idx} className="flex items-start gap-2 bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
          <Check className="w-5 h-5 flex-shrink-0 mt-0.5 text-emerald-600" />
          <span className="text-sm text-slate-700">{txt}</span>
        </div>
      ))}
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
      {[
        { v: "−12%", area: "Combustible" },
        { v: "−15%", area: "Mantenimiento" },
        { v: "−30%", area: "Seguridad" },
      ].map((m, idx) => (
        <div key={idx} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <p className="text-3xl font-black text-emerald-600">{m.v}</p>
          <p className="text-sm font-semibold text-slate-700 mt-1">{m.area}</p>
        </div>
      ))}
    </div>

    <button
      onClick={onStart}
      data-testid="calc-cta-empezar"
      className="px-10 py-4 rounded-xl font-bold text-lg text-white shadow-xl shadow-emerald-500/30 transition-all hover:scale-105"
      style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}
    >
      Calcular mi ahorro →
    </button>
  </main>
);

// ---- P1 Flota base ----
const PasoFlotaBase = ({ nVehiculos, setNVehiculos, tipoVehiculo, onChangeTipo, onBack, onNext }) => {
  const decr = () => setNVehiculos(Math.max(1, (nVehiculos || 1) - 1));
  const incr = () => setNVehiculos(Math.min(9999, (nVehiculos || 1) + 1));
  const canNext = nVehiculos > 0 && tipoVehiculo;
  return (
    <div className="space-y-8">
      <BackBtn onBack={onBack} />
      <div>
        <p className="text-lg font-semibold mb-3 text-slate-900">¿Cuántos vehículos tiene tu flota?</p>
        <div className="flex items-center gap-3">
          <button onClick={decr} className="w-10 h-10 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-700" data-testid="calc-vehiculos-minus">
            <Minus className="w-4 h-4" />
          </button>
          <input
            type="number"
            min="1"
            max="9999"
            value={nVehiculos}
            onChange={(e) => setNVehiculos(Math.max(1, Math.min(9999, parseInt(e.target.value) || 1)))}
            data-testid="calc-vehiculos-input"
            className="w-28 px-4 py-2 bg-white border border-slate-200 rounded-lg text-2xl font-black text-center text-slate-900 focus:outline-none focus:border-emerald-500"
          />
          <button onClick={incr} className="w-10 h-10 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-700" data-testid="calc-vehiculos-plus">
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div>
        <p className="text-lg font-semibold mb-3 text-slate-900">¿Qué tipo de vehículos predominan?</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {TIPOS_VEHICULO.map((t) => {
            const active = tipoVehiculo === t.value;
            return (
              <button
                key={t.value}
                onClick={() => onChangeTipo(t.value)}
                data-testid={`calc-tipo-${t.value}`}
                className="p-4 rounded-lg border-2 transition-all bg-white"
                style={{
                  background: active ? "#ecfdf5" : "#ffffff",
                  borderColor: active ? "#10b981" : "#e2e8f0",
                }}
              >
                <span className="text-2xl">{t.emoji}</span>
                <p className="text-sm font-semibold mt-1.5 text-slate-900">{t.label}</p>
              </button>
            );
          })}
        </div>
      </div>

      <button
        onClick={onNext}
        disabled={!canNext}
        data-testid="calc-p1-next"
        className="px-8 py-3 rounded-xl font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/30"
        style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}
      >
        Ver de dónde viene este ahorro →
      </button>
    </div>
  );
};

// ---- Helper: slider numérico con valor editable ----
const SliderInput = ({ label, value, setValue, min, max, step, suffix = "", fuente, testid }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1.5">
      <label className="text-sm font-semibold text-slate-800">{label}</label>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (Number.isFinite(v) && v > 0) setValue(v);
        }}
        data-testid={`${testid}-input`}
        className="w-28 text-right px-2 py-1 bg-white border border-slate-200 rounded text-base font-bold text-slate-900 focus:outline-none focus:border-emerald-500"
      />
      <span className="text-sm text-slate-500 ml-1">{suffix}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => setValue(parseFloat(e.target.value))}
      data-testid={`${testid}-slider`}
      className="w-full accent-emerald-500"
    />
    {fuente && <p className="text-[11px] italic text-slate-500 mt-1">{fuente}</p>}
  </div>
);

// ---- P2 Combustible ----
const PasoCombustible = ({ km, setKm, consumo, setConsumo, precioCombustible, setPrecioCombustible, calc, onBack, onNext, onReunion }) => (
  <div className="space-y-6">
    <BackBtn onBack={onBack} />
    <div>
      <p className="text-2xl font-black leading-tight text-slate-900">El combustible es <span className="text-emerald-600">el coste más visible</span> — y el más evitable.</p>
      <p className="text-sm text-slate-500 mt-2">Representa entre el 30% y el 40% del coste operativo de una flota.</p>
    </div>
    <div className="space-y-5 bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
      <SliderInput label="Km anuales por vehículo" value={km} setValue={setKm} min={5000} max={300000} step={1000} suffix="km" fuente="Fuente: DGT — Informe de Movilidad 2023" testid="calc-km" />
      <SliderInput label="Consumo medio" value={consumo} setValue={setConsumo} min={3} max={50} step={0.1} suffix="L/100km" fuente="Fuente: IDAE — Consumos medios por categoría" testid="calc-consumo" />
      <SliderInput label="Precio combustible" value={precioCombustible} setValue={setPrecioCombustible} min={0.8} max={3} step={0.01} suffix="€/L" fuente="Actualizable desde panel de administración" testid="calc-precio" />
    </div>
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="calc-p2-resultado">
      <p className="text-sm font-semibold leading-relaxed text-emerald-800">
        Tu flota consume <strong>{calc.litrosAnio.toLocaleString("es-ES")} litros</strong> al año a <strong>{precioCombustible.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/l</strong> — un coste de <strong>{eur(calc.costeCombustibleTotal)}</strong>. Con telemática, reducirías ese consumo un <strong>12%</strong>, ahorrando <strong>{calc.litrosAhorro.toLocaleString("es-ES")} litros</strong> y <strong>{eur(calc.ahorroCombustible)}</strong> al año.
      </p>
      <p className="text-[11px] italic text-emerald-700 mt-2">(Reducción media sector 10–15%. Fuente: DGT/IDAE 2023)</p>
    </div>

    <button onClick={onNext} data-testid="calc-p2-next" className="w-full md:w-auto px-8 py-3 rounded-xl font-bold text-white transition-all shadow-lg shadow-emerald-500/30" style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
      Ver también el ahorro en mantenimiento →
    </button>

    <CTASecundario
      mensaje="Esto es la media del sector. Con un análisis personalizado, identificamos exactamente dónde están tus oportunidades reales. ¿Hablamos?"
      textoBtn="Ya tengo suficiente — Quiero hablar con un especialista"
      onClick={onReunion}
      testid="calc-p2-cta-reunion"
    />
  </div>
);

// ---- P3 Mantenimiento ----
const PasoMantenimiento = ({ costeMant, setCosteMant, ahorroSoFar, onBack, onNext, onReunion }) => (
  <div className="space-y-6">
    <BackBtn onBack={onBack} />
    <div>
      <p className="text-2xl font-black leading-tight text-slate-900">Cada avería imprevista cuesta <span className="text-emerald-600">el doble</span> de lo que parece.</p>
      <p className="text-sm text-slate-500 mt-2">Pasar de mantenimiento correctivo a predictivo puede reducir averías imprevistas hasta un 43%.</p>
    </div>
    <div className="space-y-5 bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
      <SliderInput label="Coste mantenimiento anual/vehículo" value={costeMant} setValue={setCosteMant} min={200} max={15000} step={50} suffix="€" fuente="Fuente: ANFAC — Coste medio de mantenimiento por categoría" testid="calc-mant" />
    </div>
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
      <p className="text-sm font-semibold text-emerald-800">
        El mantenimiento no planificado es el coste más difícil de justificar — y el más evitable. Con alertas predictivas, tu flota reduciría los costes de taller un 15% de media, evitando las paradas imprevistas que más daño hacen a la operación.
      </p>
      <p className="text-[11px] italic text-emerald-700 mt-1">(Reducción media sector 12–20%. Fuente: ANFAC 2023)</p>
    </div>

    <button onClick={onNext} data-testid="calc-p3-next" className="w-full md:w-auto px-8 py-3 rounded-xl font-bold text-white transition-all shadow-lg shadow-emerald-500/30" style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
      Ver también el ahorro en seguridad →
    </button>

    <CTASecundario
      mensaje={`Llevas ${eur(ahorroSoFar)} calculados. Con un análisis personalizado, ese número puede crecer aún más.`}
      textoBtn="Quiero hablar con un especialista"
      onClick={onReunion}
      testid="calc-p3-cta-reunion"
    />
  </div>
);

// ---- P4 Seguridad ----
const PasoSeguridad = ({ nSiniestros, setNSiniestros, sinDefault, costeSiniestro, setCosteSiniestro, siniestrosMenos, ahorroSeguridad, ahorroSoFar, onBack, onNext, onReunion }) => {
  const valSiniestros = nSiniestros !== null ? nSiniestros : sinDefault;
  return (
    <div className="space-y-6">
      <BackBtn onBack={onBack} />
      <div>
        <p className="text-2xl font-black leading-tight text-slate-900">Un solo accidente puede costar <span className="text-emerald-600">entre 4.500 € y 15.000 €</span>.</p>
        <p className="text-sm text-slate-500 mt-2">Y eso sin contar el impacto en seguros, bajas y reputación. La telemática reduce la siniestralidad hasta un 35%.</p>
      </div>
      <div className="space-y-5 bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
        <SliderInput
          label="Nº siniestros al año" value={valSiniestros}
          setValue={(v) => setNSiniestros(Math.max(0, Math.round(v)))}
          min={0} max={500} step={1} suffix="/año"
          fuente="Fuente: UNESPA — Estadísticas de siniestralidad en flotas comerciales"
          testid="calc-siniestros"
        />
        <SliderInput
          label="Coste medio por siniestro" value={costeSiniestro}
          setValue={setCosteSiniestro}
          min={500} max={50000} step={100} suffix="€"
          fuente="Fuente: UNESPA — Estadísticas de siniestralidad en flotas comerciales"
          testid="calc-coste-sin"
        />
      </div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
        <p className="text-sm font-semibold text-emerald-800">
          Reduciendo la siniestralidad un 30%, tu flota evitaría {siniestrosMenos} siniestros al año y reduciría los costes asociados en {eur(ahorroSeguridad)}.
        </p>
        <p className="text-[11px] italic text-emerald-700 mt-1">(Reducción media sector 20–40%. Fuente: UNESPA 2023)</p>
      </div>

      <button onClick={onNext} data-testid="calc-p4-next" className="w-full md:w-auto px-8 py-3 rounded-xl font-bold text-white transition-all shadow-lg shadow-emerald-500/30" style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
        Ver el resumen completo →
      </button>

      <CTASecundario
        mensaje={`Llevas ${eur(ahorroSoFar)} calculados. Con un análisis personalizado, ese número puede crecer aún más.`}
        textoBtn="Quiero hablar con un especialista"
        onClick={onReunion}
        testid="calc-p4-cta-reunion"
      />
    </div>
  );
};

// ---- P5 Resumen ----
const PasoResumen = ({ calc, onBack, onVerMas, onReunion, onPDF }) => {
  const personalizadoMin = Math.round(calc.ahorroTotal * 1.15);
  const personalizadoMax = Math.round(calc.ahorroTotal * 1.20);
  const delta = personalizadoMax - calc.ahorroTotal;
  const n = Math.max(1, calc.nVehiculos || 1);
  return (
    <div className="space-y-6">
      <BackBtn onBack={onBack} />
      <div>
        <p className="text-2xl md:text-3xl font-black leading-tight text-slate-900">El ahorro total estimado <span className="text-emerald-600">para tu flota</span>.</p>
        <p className="text-sm text-slate-500 mt-2">Calculado con la media del sector para flotas como la tuya.</p>
      </div>

      {/* Comparativa */}
      <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="calc-comparativa">
        <p className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-semibold">Comparativa</p>
        <div className="space-y-2">
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-slate-600">Media sector</span>
            <span className="text-lg font-bold text-slate-900">{eur(calc.ahorroTotal)}</span>
          </div>
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-emerald-600">Análisis personalizado</span>
            <span className="text-lg font-bold text-emerald-600">{eur(personalizadoMin)} – {eur(personalizadoMax)}</span>
          </div>
          <div className="flex justify-end pt-1">
            <span className="text-xs font-bold text-emerald-600">+{eur(delta)} adicional</span>
          </div>
        </div>
      </div>

      <p className="text-sm text-slate-600 leading-relaxed">
        Cada flota tiene su propia operativa: rutas, tipo de carga, perfil de conductores, historial de siniestros. Con un análisis de 30 minutos, identificamos exactamente dónde están tus oportunidades de ahorro reales.
      </p>

      {/* Desglose */}
      <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-3" data-testid="calc-desglose">
        <FilaDesgloseArea Icon={Fuel}   label="Combustible"   total={calc.ahorroCombustible}   nVeh={n} />
        <FilaDesgloseArea Icon={Wrench} label="Mantenimiento" total={calc.ahorroMantenimiento} nVeh={n} />
        <FilaDesgloseArea Icon={Shield} label="Seguridad"     total={calc.ahorroSeguridad}     nVeh={n} />
        <div className="pt-3 mt-1 border-t border-slate-200 flex items-center justify-between">
          <span className="text-sm font-bold uppercase tracking-wider text-slate-700">Total estimado</span>
          <span className="text-lg font-black text-emerald-600" data-testid="calc-total-desglose">{eur(calc.ahorroTotal)} /año</span>
        </div>
      </div>

      <div className="rounded-2xl p-6 text-center shadow-xl shadow-emerald-500/30" style={{ background: "linear-gradient(135deg, #10b981, #059669)" }} data-testid="calc-total-box">
        <p className="text-xs uppercase tracking-wider text-emerald-50">Ahorro anual estimado</p>
        <p className="text-5xl font-black text-white mt-1">{eur(calc.ahorroTotal)}</p>
      </div>

      <div className="space-y-3">
        <button
          onClick={onVerMas}
          data-testid="calc-p5-cta-ver-mas"
          className="w-full px-8 py-5 rounded-xl font-black text-lg text-white shadow-xl shadow-emerald-500/30 transition-all hover:scale-[1.01]"
          style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}
        >
          Ver todo lo que incluye →
        </button>

        <BloquePiloto />

        <p className="text-center text-xs text-slate-500">Sin compromiso. Sin presión. Solo números.</p>

        <button
          onClick={onReunion}
          data-testid="calc-p5-cta-reunion"
          className="w-full rounded-lg text-white transition-all text-sm flex items-center justify-center gap-2 mt-2 hover:opacity-95"
          style={{ background: "#1e3a5f", padding: "13px 24px", fontWeight: 600 }}
        >
          <Phone className="w-4 h-4" />
          Quiero el análisis personalizado — Reservar 30 minutos →
        </button>

        <button
          onClick={onPDF}
          data-testid="calc-p5-cta-pdf"
          className="w-full text-sm text-slate-500 hover:text-slate-800 underline mt-2"
        >
          Descargar este resumen en PDF
        </button>
      </div>
    </div>
  );
};

// ---- P6 Valor adicional (IMPL-033) ----
const VALOR_TARJETAS = [
  { emoji: "📟", titulo: "Tacógrafo digital", texto: "Descarga automática y gestión de infracciones de tiempos de conducción. Evita sanciones de hasta 6.000 € por incumplimiento normativo." },
  { emoji: "🗺", titulo: "Gestión de rutas", texto: "Optimización de trayectos en tiempo real. Menos km en vacío, menos combustible, más entregas por jornada." },
  { emoji: "⚡", titulo: "Estudios de electrificación", texto: "Análisis de qué vehículos de tu flota son candidatos a electrificarse, cuándo y con qué retorno." },
  { emoji: "🔌", titulo: "Conexión con tus sistemas", texto: "APIs abiertas para integrar con tu ERP, TMS o sistema de facturación actual. Sin silos de información." },
  { emoji: "📊", titulo: "Informes personalizados", texto: "Informes automáticos para presentar a dirección sin trabajo manual. Listos para el CFO." },
  { emoji: "🌿", titulo: "Certificado CAES", texto: "Convierte el ahorro en combustible en ingreso directo. Tus vehículos generan créditos de carbono certificados que Mobilsafe gestiona y monetiza.", linkLabel: "Calcular mi ingreso CAES →", linkHref: "https://tuingresocaes.mobilsafe.es" },
];

const PasoValorAdicional = ({ onBack, onReunion }) => (
  <div className="space-y-6" data-testid="calc-paso-6-content">
    <BackBtn onBack={onBack} />
    <div>
      <p className="text-2xl md:text-3xl font-black leading-tight text-slate-900">
        Con la misma plataforma, <span className="text-emerald-600">sin coste adicional.</span>
      </p>
      <p className="text-sm text-slate-500 mt-2">
        La telemática es la base. Lo que construyes encima es donde está el valor real.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="calc-p6-grid">
      {VALOR_TARJETAS.map((t, i) => (
        <div
          key={i}
          className="bg-white border border-slate-200 hover:border-emerald-300 rounded-xl p-5 shadow-sm hover:shadow-md transition-all"
          data-testid={`calc-p6-card-${i}`}
        >
          <div className="text-2xl mb-2">{t.emoji}</div>
          <h3 className="text-base font-bold text-slate-900 mb-2">{t.titulo}</h3>
          <p className="text-sm text-slate-600 leading-relaxed">{t.texto}</p>
          {t.linkHref && (
            <a
              href={t.linkHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`calc-p6-link-${i}`}
              className="inline-block mt-3 text-sm font-semibold underline text-emerald-600 hover:text-emerald-700"
            >
              {t.linkLabel}
            </a>
          )}
        </div>
      ))}
    </div>

    <div className="space-y-3 pt-2">
      <button
        onClick={onReunion}
        data-testid="calc-p6-cta-reunion"
        className="w-full px-8 py-5 rounded-xl font-black text-lg text-white shadow-xl shadow-emerald-500/30 transition-all hover:scale-[1.01]"
        style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}
      >
        Quiero el análisis personalizado — Reservar 30 minutos →
      </button>

      <BloquePiloto />

      <p className="text-center text-xs text-slate-500">Sin compromiso. Sin presión. Solo números.</p>
    </div>
  </div>
);

// ---- Bloque piloto (IMPL-033) — elemento de confianza, no CTA ----
const BloquePiloto = () => (
  <div
    className="rounded-lg p-4 flex items-start gap-3"
    style={{ background: "rgba(30,58,95,0.06)", border: "1px solid #1e3a5f" }}
    data-testid="calc-bloque-piloto"
  >
    <span className="text-xl leading-none mt-0.5">🧪</span>
    <div className="text-[13px] text-slate-700 leading-relaxed">
      <p>
        <strong className="text-slate-900">Empezamos con un piloto de 45–60 días</strong> en una muestra de tu flota.
      </p>
      <p className="mt-1 text-slate-500">
        Sin compromiso de despliegue total hasta que los números sean reales en tu operación.
      </p>
    </div>
  </div>
);

// ============================================
// Componentes auxiliares
// ============================================

const BackBtn = ({ onBack }) => (
  <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5 transition-all" data-testid="calc-back-btn">
    <ChevronLeft className="w-4 h-4" /> Volver
  </button>
);

// IMPL-033 · fila de desglose: emoji + label · €/veh × n = total
const FilaDesgloseArea = ({ Icon, emoji, label, total, nVeh }) => {
  const porVehiculo = Math.round((total || 0) / Math.max(1, nVeh));
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <div className="flex items-center gap-2 flex-shrink-0">
        {Icon ? <Icon className="w-4 h-4 text-slate-400" /> : emoji && <span className="text-base">{emoji}</span>}
        <span className="text-slate-700 font-medium">{label}</span>
      </div>
      <div className="text-right">
        <span className="text-xs text-slate-500">{eur(porVehiculo)}/veh × {nVeh} = </span>
        <span className="font-bold text-emerald-600">{eur(total)}</span>
      </div>
    </div>
  );
};

const CTASecundario = ({ mensaje, textoBtn, onClick, testid }) => (
  <div
    className="rounded-lg p-5 mt-2"
    style={{ background: "#1e3a5f", border: "1.5px solid #1e3a5f", boxShadow: "0 0 0 1px rgba(255,255,255,0.06) inset" }}
    data-testid="cta-s-wrap"
  >
    <p className="text-sm text-slate-100 mb-3 leading-relaxed">{mensaje}</p>
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full md:w-auto rounded-lg text-white border border-white/30 hover:bg-white/10 transition-all text-sm flex items-center justify-center gap-2"
      style={{ padding: "13px 24px", fontWeight: 600 }}
    >
      <Phone className="w-4 h-4" />
      {textoBtn} →
    </button>
  </div>
);

// ============================================
// MODALES
// ============================================

const ModalReunion = ({ modalDesde, calc, datosLead, onClose }) => {
  const [form, setForm] = useState({ nombre: "", empresa: "", email: "", telefono: "", disponibilidad: "", mensaje: "" });
  const [intereses, setIntereses] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const toggleInteres = (it) => {
    setIntereses((prev) =>
      prev.includes(it) ? prev.filter((x) => x !== it) : [...prev, it]
    );
  };

  const contextoPorPantalla = {
    P2: `Llevas ${eur(calc.ahorroCombustible)} calculados en combustible.`,
    P3: `Llevas ${eur(calc.ahorroCombustible + calc.ahorroMantenimiento)} calculados (combustible + mantenimiento).`,
    P4: `Llevas ${eur(calc.ahorroSoFar)} calculados (combustible + mantenimiento + seguridad).`,
    P5: `Tu ahorro total estimado: ${eur(calc.ahorroTotal)}.`,
    P6: `Tu ahorro total estimado: ${eur(calc.ahorroTotal)}.`,
  };
  const ctx = contextoPorPantalla[modalDesde] || `Tu ahorro estimado: ${eur(calc.ahorroSoFar)}.`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return toast.error("Introduce tu nombre");
    if (!form.empresa.trim()) return toast.error("Introduce el nombre de la empresa");
    if (!form.email.trim()) return toast.error("Introduce tu email");
    if (!form.telefono.trim()) return toast.error("Introduce tu teléfono");
    if (!form.disponibilidad) return toast.error("Selecciona tu disponibilidad");
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        origen: "calculadora",
        tipo_cta: "reunion",
        pantalla_origen: modalDesde,
        nombre: form.nombre,
        empresa: form.empresa,
        email: form.email,
        telefono: form.telefono,
        disponibilidad: form.disponibilidad,
        mensaje: form.mensaje || null,
        intereses_adicionales: intereses,
        datos: datosLead,
      });
      // IMPL-062 · evento de conversión al funnel (paso final del embudo)
      trackFunnelEvent("calculadora", "lead_captured", { tipo: "reunion", pantalla: modalDesde });
      setDone(true);
    } catch {
      toast.error("Error al enviar. Inténtalo de nuevo.");
    }
    setSending(false);
  };

  return (
    <ModalShell onClose={onClose} testid="calc-modal-reunion">
      {done ? (
        <ConfirmacionEnvio onClose={onClose} mensaje="Perfecto. Te contactamos en menos de 24 horas para confirmar la reunión." />
      ) : (
        <>
          <h3 className="text-2xl font-black mb-3 text-slate-900">Reserva tu análisis de 30 minutos</h3>
          <div className="rounded-lg p-3 mb-5 bg-emerald-50 border border-emerald-200">
            <p className="text-sm text-emerald-800">{ctx}</p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <FieldInput label="Nombre y apellidos *" value={form.nombre} onChange={(v) => setForm({ ...form, nombre: v })} testid="modal-r-nombre" />
            <FieldInput label="Empresa *" value={form.empresa} onChange={(v) => setForm({ ...form, empresa: v })} testid="modal-r-empresa" />
            <FieldInput label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="modal-r-email" />
            <FieldInput label="Teléfono *" type="tel" value={form.telefono} onChange={(v) => setForm({ ...form, telefono: v })} testid="modal-r-telefono" />
            <div>
              <label className="block text-xs uppercase text-slate-500 mb-1.5 font-semibold">Disponibilidad *</label>
              <div className="grid grid-cols-3 gap-2">
                {["mañanas", "tardes", "indiferente"].map((d) => (
                  <button
                    key={d} type="button"
                    onClick={() => setForm({ ...form, disponibilidad: d })}
                    data-testid={`modal-r-disp-${d}`}
                    className="py-2 rounded-lg text-sm font-semibold transition-all capitalize border bg-white"
                    style={{
                      background: form.disponibilidad === d ? "#ecfdf5" : "#ffffff",
                      borderColor: form.disponibilidad === d ? "#10b981" : "#e2e8f0",
                      color: form.disponibilidad === d ? "#059669" : "#475569",
                    }}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase text-slate-500 mb-1.5 font-semibold">¿Hay algo concreto que quieras analizar?</label>
              <textarea
                rows={3}
                value={form.mensaje}
                onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
                data-testid="modal-r-mensaje"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-emerald-500"
              />
            </div>

            {/* IMPL-033 · intereses adicionales (opcional, multi-select) */}
            <div data-testid="modal-r-intereses">
              <label className="block text-xs uppercase text-slate-500 mb-2 font-semibold">¿Qué más te interesa explorar? (opcional)</label>
              <div className="space-y-2">
                {INTERESES_OPTIONS.map((opt) => {
                  const active = intereses.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => toggleInteres(opt)}
                      data-testid={`modal-r-int-${opt.split(" ")[0].toLowerCase()}`}
                      className="w-full flex items-start gap-2 px-3 py-2 rounded-lg border text-left text-sm transition-all"
                      style={{
                        background: active ? "#ecfdf5" : "#ffffff",
                        borderColor: active ? "#10b981" : "#e2e8f0",
                        color: active ? "#059669" : "#475569",
                      }}
                    >
                      <span
                        className="mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0"
                        style={{
                          background: active ? "#10b981" : "transparent",
                          borderColor: active ? "#10b981" : "#cbd5e1",
                        }}
                      >
                        {active && <Check className="w-3 h-3 text-white" />}
                      </span>
                      <span className="leading-snug">{opt}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="modal-r-submit"
              className="w-full py-3 rounded-lg font-bold text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30"
              style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {sending ? "Enviando..." : "Reservar reunión"}
            </button>
          </form>
        </>
      )}
    </ModalShell>
  );
};

const ModalPDF = ({ calc, datosLead, onClose, onSwitchReunion }) => {
  const [form, setForm] = useState({ nombre: "", email: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return toast.error("Introduce tu nombre");
    if (!form.email.trim()) return toast.error("Introduce tu email");
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        origen: "calculadora",
        tipo_cta: "pdf",
        pantalla_origen: "P5",
        nombre: form.nombre,
        empresa: "",
        email: form.email,
        telefono: null,
        datos: datosLead,
      });
      // IMPL-062 · evento de conversión al funnel (vertiente PDF)
      trackFunnelEvent("calculadora", "lead_captured", { tipo: "pdf", pantalla: "P5" });
      setDone(true);
    } catch {
      toast.error("Error al enviar. Inténtalo de nuevo.");
    }
    setSending(false);
  };

  return (
    <ModalShell onClose={onClose} testid="calc-modal-pdf">
      {done ? (
        <ConfirmacionEnvio
          onClose={onClose}
          mensaje="Listo. Te enviamos el PDF al email en unos minutos."
          extraNode={
            <button onClick={onSwitchReunion} className="text-sm text-slate-600 hover:text-slate-900 underline mt-3" data-testid="modal-pdf-switch-reunion">
              ¿Prefieres hablar con un especialista?
            </button>
          }
        />
      ) : (
        <>
          <h3 className="text-2xl font-black mb-4 text-slate-900">Descargar tu resumen en PDF</h3>
          <form onSubmit={submit} className="space-y-3">
            <FieldInput label="Nombre *" value={form.nombre} onChange={(v) => setForm({ ...form, nombre: v })} testid="modal-pdf-nombre" />
            <FieldInput label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="modal-pdf-email" />
            <button
              type="submit"
              disabled={sending}
              data-testid="modal-pdf-submit"
              className="w-full py-3 rounded-lg font-bold text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30"
              style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {sending ? "Enviando..." : "Enviarme el PDF"}
            </button>
          </form>
        </>
      )}
    </ModalShell>
  );
};

const ModalShell = ({ children, onClose, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={onClose} data-testid={testid}>
    <div
      className="relative max-w-md w-full max-h-[90vh] overflow-y-auto rounded-xl p-6 border bg-white border-slate-200 shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <button onClick={onClose} className="absolute top-3 right-3 text-slate-400 hover:text-slate-900" data-testid="modal-close-btn">
        <X className="w-5 h-5" />
      </button>
      {children}
    </div>
  </div>
);

const FieldInput = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="block text-xs uppercase text-slate-500 mb-1.5 font-semibold">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-emerald-500"
    />
  </div>
);

const ConfirmacionEnvio = ({ mensaje, onClose, extraNode }) => (
  <div className="text-center py-4">
    <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg shadow-emerald-500/30" style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
      <Check className="w-7 h-7 text-white" />
    </div>
    <p className="text-base text-slate-900 leading-relaxed">{mensaje}</p>
    {extraNode}
    <button onClick={onClose} data-testid="modal-confirm-close" className="mt-5 px-6 py-2 rounded-lg text-sm font-semibold text-white bg-slate-700 hover:bg-slate-800">
      Cerrar
    </button>
  </div>
);

export default CalculadoraAhorro;
